import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RelatedPartyChangeSystemAcctService } from './related-party-change-system-acct.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-related-party-change-system-acct',
    templateUrl: './related-party-change-system-acct.component.html',
    styleUrls: ['./related-party-change-system-acct.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RelatedPartyChangeSystemAcctComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Component specific properties
  modalTitle = "Related Party Classification";
  categoryList: string[] = [];
  selectedCategory: string = "";

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  private categoriesLoaded = false; // Prevent duplicate loading

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private componentService: RelatedPartyChangeSystemAcctService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.init(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.init(); }));
    this.init();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = { ...baseUrls, ...(header.generalConfigData || {}) };
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || '';
  }

  init() {
    try {
      const hasData = (this.rowData && Object.keys(this.rowData).length > 0) ||
                      (this.globalService && Object.keys(this.globalService).length > 0);
      const hasBaseUrl = this.resolveApiBase();

      if (hasData && hasBaseUrl && !this.categoriesLoaded) {
        this.loadCategories();
      }
    } catch (e) {
      console.error('Error in RelatedPartyChangeSystemAcct init:', e);
    }
  }

  loadCategories() {
    this.categoriesLoaded = true;

    this.componentService.loadCategories(
      this.resolveApiBase(),
      this.globalService,
      this.filterParams,
      this.rowData,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === "1") {
          this.categoryList = [];
          if (response.jsonObject && Array.isArray(response.jsonObject)) {
            this.categoryList = response.jsonObject.map((item: any) => item.LABEL);
          }
          this.cdr.detectChanges();
        } else {
          if (response.errorMessage === "no access") {
            this._snackBar.open('Sorry you do not have access to do the requested action.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
      },
      (error: any) => {
        console.error('Error loading categories:', error);
        this._snackBar.open('Error loading categories. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  save() {
    if (!this.selectedCategory) {
      this._snackBar.open('Please select a category', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const clob_settings = [{
      CATEGORY: this.selectedCategory,
      CHART_KEY: this.rowData.CHART_KEY,
      ACCT_KEY: this.rowData.ACCT_KEY
    }];

    this.componentService.saveCategoryUpdate(
      this.resolveApiBase(),
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Acct Category Updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}

